export interface BudgetLineAmountsInput {
  quantity?: number | null;
  /** `originalPriceUnit`: va en la divisa de la LINEA, no en la del presupuesto. */
  priceUnit?: number | null;
  /** `convertedPriceUnit`: la misma divisa que el resto de la fila (CEC-632). */
  convertedPriceUnit?: number | null;
  priceSubtotal?: number | null;
  priceTotal?: number | null;
  discount?: number | null;
}

/**
 * Importes de una línea de presupuesto, resueltos sobre BASE NETA (CEC-586).
 *
 * Las tres funciones devuelven valores SIN redondear: el redondeo es cosa de la
 * plantilla, que imprime el precio unitario con 4 decimales y los importes con 2.
 *
 * Reparto de responsabilidades:
 *   - precio unitario  -> neto y ANTES de descuento, igual que la pantalla de alta
 *   - importe neto     -> DESPUÉS de descuento, porque es el que suma al Subtotal
 *   - importe bruto    -> lo que ya emitía el backend; no se recalcula
 *
 * El impuesto no se calcula aquí: vive en budget-preview-tax-summary.utils.ts.
 */

/**
 * Precio unitario neto, sin impuestos y sin descuento aplicado, EN LA DIVISA DEL
 * PRESUPUESTO.
 *
 * Prefiere `convertedPriceUnit` (CEC-632). `priceUnit` es el precio ORIGINAL, en
 * la divisa de la linea, mientras que `priceSubtotal` y `priceTotal` ya llegan
 * convertidos: leer el original dejaba dos monedas en la misma fila y el
 * documento solo anuncia una. Medido en S814476 linea 541, y en 71 de las 300
 * lineas de DEV.
 *
 * Preferirlo SIEMPRE es correcto y no hace falta comparar divisas: cuando la
 * linea va en la del presupuesto, las tres versiones del precio son identicas.
 *
 * El respaldo a `priceUnit` es una red, no una solucion a un caso real: de las
 * 300 lineas medidas solo una carece de convertido, y va en la misma divisa que
 * su presupuesto, asi que el respaldo le da el mismo numero.
 *
 * Si tampoco hay precio, lo deriva del subtotal neto — nunca de un importe
 * bruto, que es el defecto que corrigio CEC-586.
 */
export function resolveNetUnitPrice(line: BudgetLineAmountsInput): number {
  const convertedPriceUnit = toNumber(line.convertedPriceUnit);
  if (convertedPriceUnit) {
    return convertedPriceUnit;
  }

  const priceUnit = toNumber(line.priceUnit);
  if (priceUnit) {
    return priceUnit;
  }

  const quantity = toNumber(line.quantity);
  const priceSubtotal = toNumber(line.priceSubtotal);
  if (!quantity || !priceSubtotal) {
    return priceUnit;
  }

  return priceSubtotal / quantity;
}

/**
 * Importe neto de la línea, con el descuento ya aplicado.
 *
 * REGLA (verificada contra el documento real S814476 de DEV, 26-08-2026):
 * `priceSubtotal` llega del backend **ya descontado**, mientras que
 * `cantidad × precio unitario` es **bruto**. El descuento solo se aplica en el
 * segundo caso; hacerlo también sobre `priceSubtotal` lo contaba dos veces.
 *
 * La prueba está en el propio documento: el Importe base impreso (2.112,16) solo
 * cuadra si la línea con 2 % de descuento aporta 1.960,00 y no 2.000,00; y su
 * Importe (2.156,00) es exactamente 1.960 × 1,10.
 */
export function resolveNetLineAmount(line: BudgetLineAmountsInput): number {
  const priceSubtotal = toNumber(line.priceSubtotal);
  if (priceSubtotal) {
    return priceSubtotal;
  }

  const gross = toNumber(line.quantity) * toNumber(line.priceUnit);
  return gross - (gross * toNumber(line.discount) / 100);
}

/**
 * Importe neto ANTES de descuento — lo que se imprime como «Importe base».
 *
 * Se reconstruye desde el neto ya descontado, no desde `cantidad × precio`, para
 * que «Importe base − Subtotal» dé exactamente el descuento aunque los datos de la
 * línea sean incoherentes entre sí.
 */
export function resolveNetLineAmountBeforeDiscount(line: BudgetLineAmountsInput): number {
  const net = resolveNetLineAmount(line);
  const discount = toNumber(line.discount);
  if (!discount || discount >= 100) {
    return net;
  }

  return net / (1 - discount / 100);
}

/**
 * Importe con impuestos incluidos: el valor que ya se imprimía en la columna
 * Importe. Se respeta `priceTotal` del backend para que los PDF ya enviados al
 * cliente sigan siendo consistentes con los nuevos.
 */
export function resolveGrossLineAmount(line: BudgetLineAmountsInput): number {
  const priceTotal = toNumber(line.priceTotal);
  if (priceTotal) {
    return priceTotal;
  }

  return resolveNetLineAmount(line);
}

/**
 * Porcentaje de descuento de la linea, ya coaccionado a numero.
 *
 * La plantilla decide con esto si pinta la nota «Desc. X%». Devolver siempre un
 * numero evita dos fallos silenciosos: que un "0" en texto se lea como verdadero
 * en un @if, y que un valor no numerico llegue al DecimalPipe y tumbe el render.
 */
export function resolveLineDiscountPercent(line: BudgetLineAmountsInput): number {
  return toNumber(line.discount);
}

/**
 * El componente alimenta estas funciones con `getParsedValue()`, que devuelve el
 * valor CRUDO cuando no es un objeto {parsedValue}: la API puede colar números en
 * texto. Se coacciona igual que hacía la aritmética anterior, en vez de rechazarlos
 * y dejar los importes a cero en silencio.
 */
function toNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * ¿Hay que desglosar el descuento en el bloque de totales? (CEC-617)
 *
 * El bloque imprime «Importe base» y «Descuento» SOLO cuando hay descuento. Sin
 * él las dos filas repetirían el Subtotal al céntimo, que es la duplicidad que
 * el cliente pidió quitar.
 *
 * La decisión se toma sobre el valor YA REDONDEADO A CÉNTIMOS, no sobre la
 * diferencia en crudo. Dos motivos, y ninguno es teórico:
 *
 *   - `resolveNetLineAmountBeforeDiscount()` reconstruye dividiendo, así que deja
 *     polvo de coma flotante: sin redondear, un documento sin descuentos podría
 *     sacar la fila por una diferencia de 1e-13.
 *   - Un descuento por debajo de medio céntimo se imprimiría como «Descuento
 *     0,00» entre dos importes que se leen idénticos. Peor que no enseñarlo.
 *
 * Una diferencia negativa —dato incoherente— tampoco desglosa: un «Descuento»
 * que suma en vez de restar no explica nada.
 */
export function shouldDiscloseBudgetDiscount(baseAmount: number, netAmount: number): boolean {
  const discountInCents = Math.round((toNumber(baseAmount) - toNumber(netAmount)) * 100);

  return discountInCents >= 1;
}
